import React, { useState, useEffect } from 'react';
import { get, isEmpty } from 'lodash';
import apiClient, { WrappingApiClient } from 'libs/api/apiClient';
import { handleFieldError } from 'libs/api/errorHandle';
import { ESubject, CSubject } from 'modules/learning';
import { Checkbox, Form, Typography, Col, Row, Modal, Button, Input, message, InputNumber } from 'antd';
import styled from 'styled-components';
import { FormWrapper } from 'GlobalStyles';

type TModalData = {
  board_number?: number | null;
  subject: string;
  is_example: boolean;
  subtitle: string;
  exercise_button: string;
  subcategory: string | null;
  mentoring_field: string;
};

const EditLearningBoardModal = ({ modalState, setModalState, record }: any) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState<boolean>(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(false);
  const [isExample, setIsExample]: any = useState();
  const [subject, setSubject]: any = useState();
  const [subcategory, setSubcategory]: any = useState();
  const [mentoringField, setMentoringField]: any = useState();
  const { Title } = Typography;

  const getSubcategoryList = (theme: string) => {
    if (theme) {
      return WrappingApiClient(apiClient.get(`/drill/board/subcategories?subject=${theme}`));
    }
  };

  useEffect(() => {
    const getSubcategories = async () => {
      if (subject) {
        const { success, data }: any = await getSubcategoryList(subject);
        if (success) setSubcategory(data);
      } else {
        if (get(record, 'subcategory.id') || get(record, 'subcategory.id') === null) {
          const { success, data }: any = await getSubcategoryList(get(record, 'subject'));
          if (success) setSubcategory(data);
        }
      }
    };
    mentoringField === 'consulting' ? getSubcategories() : setSubcategory([]);
  }, [subject]);

  useEffect(() => {
    if (isEmpty(record) === false) {
      form.setFieldsValue({
        board_number: get(record, 'board_number'),
        subject: [get(record, 'subject')],
        subcategory: [get(record, 'subcategory.id')],
        subtitle: get(record, 'subtitle'),
        is_example: [get(record, 'is_example').toString()],
        exercise_button: [get(record, 'exercise_button')],
        mentoring_field: [get(record, 'mentoring_field')],
      });

      if (form.getFieldValue('is_example') === false) {
        setIsExample(false);
      } else {
        setIsExample(true);
      }

      if (get(record, 'is_example') === true) {
        setIsExample(true);
      } else {
        setIsExample(false);
      }
    }
  }, []);

  useEffect(() => {
    setVisible(modalState.visible);
  }, [modalState.visible]);

  function handleCancel() {
    setModalState({ visible: false });
  }

  const checkLimitNum = (value: any) => {
    const checkSubjectValue = form.getFieldValue(value);
    if (checkSubjectValue !== undefined && checkSubjectValue.length > 1) {
      message.error('주제는 한 개만 체크할 수 있습니다');
    }
  };

  function onClickSubmit() {
    form.validateFields().then((value) => {
      handleSubmit(value as any);
    });

    async function handleSubmit(value: any) {
      try {
        setSubmitButtonDisabled(true);

        const value: TModalData = {
          board_number: null,
          subject: '',
          is_example: false,
          subtitle: '',
          exercise_button: 'top',
          subcategory: null,
          mentoring_field: '',
        };

        const board_number = form.getFieldValue('board_number');
        if (board_number) {
          value.board_number = board_number;
        }

        const subject = form.getFieldValue('subject');
        if (subject) {
          value.subject = subject[0];
        }
        const isExample = form.getFieldValue('is_example');
        if (isExample) {
          value.is_example = isExample[0];
        }
        const subtitle = form.getFieldValue('subtitle');
        if (subtitle) {
          value.subtitle = subtitle;
        }

        const subcategory = form.getFieldValue('subcategory');
        if (subcategory[0]) {
          value.subcategory = subcategory.toString();
        } else {
          value.subcategory = null;
        }

        const mentoringField = form.getFieldValue('mentoring_field');
        if (mentoringField[0]) {
          value.mentoring_field = mentoringField[0];
        }

        if (isExample[0] === 'true') {
          const exerciseBtn = form.getFieldValue('exercise_button');
          if (exerciseBtn) {
            value.exercise_button = exerciseBtn[0];
          }
        } else {
          value.exercise_button = 'top';
        }

        modalState.onFinish && modalState.onFinish(value);
        handleCancel();
      } catch (e) {
        handleFieldError(e, form);
        throw e;
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
  }

  return (
    <Modal
      visible={visible}
      centered={true}
      maskClosable={true}
      closable={true}
      footer={
        <Row gutter={[8, 8]} justify='end'>
          <Col>
            <Button onClick={handleCancel}>취소</Button>
            <Button type='primary' onClick={onClickSubmit} loading={submitButtonDisabled}>
              적용
            </Button>
          </Col>
        </Row>
      }
      width={540}
      onCancel={handleCancel}
    >
      <FormWrapper style={{ padding: '0px' }}>
        <StyledSettingQuizInfoWrapper>
          <Title level={3}>기본정보 편집</Title>

          <Form
            form={form}
            name='LearningBoardModal'
            colon={false}
            className='form-normal'
            style={{ margin: '10px' }}
          >
            <Row>
              <Form.Item
                name={'mentoring_field'}
                label='필드'
                rules={[{ required: true, message: 'Please check subject' }]}
                style={{ marginBottom: '10px' }}
              >
                <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('mentoring_field')}>
                  <Checkbox value='consulting' onClick={() => setMentoringField('consulting')}>
                    Consulting
                  </Checkbox>
                </Checkbox.Group>
              </Form.Item>
              <Form.Item
                name={'mentoring_field'}
                rules={[{ required: true, message: 'Please check subject' }]}
                style={{ marginBottom: '10px' }}
              >
                <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('mentoring_field')}>
                  <Checkbox value='finance' onClick={() => setMentoringField('finance')}>
                    Finance
                  </Checkbox>
                </Checkbox.Group>
              </Form.Item>
            </Row>

            {mentoringField === 'consulting' && (
              <Row>
                <Form.Item
                  name={'subject'}
                  label='주제'
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox value={ESubject.FitInterview} onClick={() => setSubject(ESubject.FitInterview)}>
                      {CSubject[ESubject.FitInterview]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.CaseInterview}
                      onClick={() => setSubject(ESubject.CaseInterview)}
                    >
                      {CSubject[ESubject.CaseInterview]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.TurnaroundStrategy}
                      onClick={() => setSubject(ESubject.TurnaroundStrategy)}
                    >
                      {CSubject[ESubject.TurnaroundStrategy]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.GrowthStrategy}
                      onClick={() => setSubject(ESubject.GrowthStrategy)}
                    >
                      {CSubject[ESubject.GrowthStrategy]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.DdMarketSizing}
                      onClick={() => setSubject(ESubject.DdMarketSizing)}
                    >
                      {CSubject[ESubject.DdMarketSizing]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.OperationOptimization}
                      onClick={() => setSubject(ESubject.OperationOptimization)}
                    >
                      {CSubject[ESubject.OperationOptimization]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.SalesMarketingStrategy}
                      onClick={() => setSubject(ESubject.SalesMarketingStrategy)}
                    >
                      {CSubject[ESubject.SalesMarketingStrategy]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
              </Row>
            )}

            {mentoringField === 'finance' && (
              <Row>
                <Form.Item
                  name={'subject'}
                  label='주제'
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox value={ESubject.BasicGuide} onClick={() => setSubject(ESubject.BasicGuide)}>
                      {CSubject[ESubject.BasicGuide]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.ResumeBasedQuestions}
                      onClick={() => setSubject(ESubject.ResumeBasedQuestions)}
                    >
                      {CSubject[ESubject.ResumeBasedQuestions]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.IBIndustryInsights}
                      onClick={() => setSubject(ESubject.IBIndustryInsights)}
                    >
                      {CSubject[ESubject.IBIndustryInsights]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.PersonalityTraits}
                      onClick={() => setSubject(ESubject.PersonalityTraits)}
                    >
                      {CSubject[ESubject.PersonalityTraits]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.CommercialAcumen}
                      onClick={() => setSubject(ESubject.CommercialAcumen)}
                    >
                      {CSubject[ESubject.CommercialAcumen]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  name={'subject'}
                  rules={[{ required: true, message: 'Please check subject' }]}
                  style={{ marginBottom: '10px' }}
                >
                  <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                    <Checkbox
                      value={ESubject.FinalQuestions}
                      onClick={() => setSubject(ESubject.FinalQuestions)}
                    >
                      {CSubject[ESubject.FinalQuestions]}
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
              </Row>
            )}

            <Row>
              <Form.Item
                name='is_example'
                label='유형'
                rules={[{ required: true, message: 'Please check' }]}
                style={{ marginTop: '10px' }}
              >
                <Checkbox.Group
                  className='checkbox-btn'
                  onChange={() => checkLimitNum('is_example')}
                  disabled={get(record, 'is_example') === true}
                >
                  <Checkbox value='false' onClick={() => setIsExample(false)}>
                    설명글
                  </Checkbox>
                </Checkbox.Group>
              </Form.Item>

              {mentoringField === 'consulting' && (
                <Form.Item
                  name='is_example'
                  rules={[{ required: true, message: 'Please check' }]}
                  style={{ marginTop: '10px' }}
                >
                  <Checkbox.Group
                    className='checkbox-btn'
                    onChange={() => checkLimitNum('is_example')}
                    disabled={get(record, 'is_example') === false}
                  >
                    <Checkbox value='true' onClick={() => setIsExample(true)}>
                      예제
                    </Checkbox>
                  </Checkbox.Group>
                </Form.Item>
              )}
            </Row>

            <Row>
              <Form.Item name='board_number' label='글순서' className='form-item'>
                <InputNumber min={1} />
              </Form.Item>
            </Row>

            <Row>
              <Form.Item
                name={'subcategory'}
                style={{ marginBottom: '10px' }}
                label={'소주제'}
                rules={[{ required: true, message: 'Please check' }]}
              >
                <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                  <Checkbox value={null}>없음</Checkbox>
                </Checkbox.Group>
              </Form.Item>
              {subcategory &&
                subcategory.map(({ id, name }: any) => (
                  <Form.Item
                    name={'subcategory'}
                    style={{ marginBottom: '10px' }}
                    rules={[{ required: true, message: 'Please check' }]}
                  >
                    <Checkbox.Group className='checkbox-btn' onChange={() => checkLimitNum('subject')}>
                      <Checkbox value={id}>{name}</Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                ))}
            </Row>

            <Form.Item
              name='subtitle'
              label='한줄설명'
              className='form-item'
              rules={[{ required: true, message: 'Please input' }]}
            >
              <Input size='large' placeholder='Text' />
            </Form.Item>

            <Row>
              {isExample && (
                <>
                  <Form.Item
                    name={'exercise_button'}
                    label='버튼위치'
                    rules={[{ required: true, message: 'Please check' }]}
                  >
                    <Checkbox.Group
                      className='checkbox-btn'
                      onChange={() => checkLimitNum('exercise_button')}
                    >
                      <Checkbox value='top'>Top</Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                  <Form.Item name={'exercise_button'} rules={[{ required: true, message: 'Please check' }]}>
                    <Checkbox.Group
                      className='checkbox-btn'
                      onChange={() => checkLimitNum('exercise_button')}
                    >
                      <Checkbox value='middle'>Middle</Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                  <Form.Item name={'exercise_button'} rules={[{ required: true, message: 'Please check' }]}>
                    <Checkbox.Group
                      className='checkbox-btn'
                      onChange={() => checkLimitNum('exercise_button')}
                    >
                      <Checkbox value='bottom'>Bottom</Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                </>
              )}
            </Row>
          </Form>
        </StyledSettingQuizInfoWrapper>
      </FormWrapper>
    </Modal>
  );
};

export default EditLearningBoardModal;

const StyledSettingQuizInfoWrapper = styled.div`
  & .ant-form-item-label > label {
    width: 70px;
  }
`;
